import { FeatureCollection, MultiPolygon, Polygon } from 'geojson'
import { useEffect, useMemo, useState } from 'react'
import { SpaceGeoStandardId, SpaceGeoStandardType } from '~/packages/general/variable/spaceTime.types'
import { BaseActionDefinitionEnhanced } from '~/prix'
import useAction from '~/prix/react/hooks/action'
import { omit } from '~/prix/utils/types/object'
import createBbox from '@turf/bbox'
import { BaseItem } from '~/components/editCoverageArea'
import { z } from 'zod'

export interface Delimitation {
  id: SpaceGeoStandardId
  key?: string | null
  name: string
  type: SpaceGeoStandardType | string
  shape: Polygon | MultiPolygon
}

export function useDelimitation<
  Item extends BaseItem,
  InSchema extends z.ZodType<any, any, any>,
  OutSchema extends z.ZodType<any, any, any>,
>(
  fetchDefinition: BaseActionDefinitionEnhanced<InSchema, OutSchema>,
  setCoveragesFeatureCollection: React.Dispatch<
    React.SetStateAction<FeatureCollection<Polygon | null, Omit<Item, 'coverageArea'>> | null>
  >,
  setIsCompatibleDelimitation?: React.Dispatch<React.SetStateAction<boolean>>,
  compatibleDelimitationTypes: string[] = ['municipality'],
  refreshKey?: number,
  indexes?: { startIndex: number; endIndex: number },
  search?: string,
) {
  type FeatureProperties = Omit<Item, 'coverageArea'>

  const [delimitation, setDelimitation] = useState<Delimitation | null>(null)

  const isCompatibleDelimitation = useMemo(() => {
    if (!delimitation) {
      return false
    }
    return compatibleDelimitationTypes.includes(delimitation.type)
  }, [compatibleDelimitationTypes, delimitation])

  const currentResult = useAction(
    fetchDefinition,
    isCompatibleDelimitation && delimitation
      ? {
          intersection: delimitation.shape,
          delimitationId: delimitation.id,
          delimitationType: delimitation.type,
          delimitationKey: delimitation.key,
          municipalityId: delimitation.type === 'municipality' ? delimitation.id : undefined,
          startIndex: indexes?.startIndex ?? 0,
          stopIndex: indexes?.endIndex ?? 200,
          search,
        }
      : undefined,
    [delimitation, refreshKey, search, indexes],
    {
      autoLoad: isCompatibleDelimitation,
    },
  )

  useEffect(() => {
    if (!currentResult.result) {
      return
    }
    const features = currentResult.result.items?.map(
      (item: Item & { coverageArea: Polygon | MultiPolygon | null }) => {
        return {
          type: 'Feature',
          properties: omit(item, ['coverageArea']),
          geometry: item.coverageArea ?? null,
        }
      },
    )

    setCoveragesFeatureCollection({
      type: 'FeatureCollection',
      features,
    } as FeatureCollection<Polygon | null, FeatureProperties>)
  }, [currentResult.result])

  useEffect(() => {
    setIsCompatibleDelimitation?.(isCompatibleDelimitation)
  }, [isCompatibleDelimitation, setIsCompatibleDelimitation])

  const mapPosition = useMemo(() => {
    if (!delimitation?.shape) return

    const bbox = createBbox(delimitation.shape)
    const bounds: google.maps.LatLngBoundsLiteral = {
      west: bbox[0],
      south: bbox[1],
      east: bbox[2],
      north: bbox[3],
    }
    return { bounds }
  }, [delimitation])

  return { delimitation, setDelimitation, currentResult, mapPosition }
}
